import { HasAccess } from "@permify/react-role";
import { Badge, Button, Col, DatePicker, Form, Input, Row, Select, Spin, Tabs, message } from 'antd';
import locale from 'antd/es/date-picker/locale/es_ES';
import * as _ from 'lodash';
import moment from 'moment';
import 'moment/locale/es';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getBranchOffices } from '../../services/general';
import {
  getForecastPredictionsByIds,
  getReservationServices,
  summarizedPatientsByReservation,
} from '../../services/reservation';
import ConfirmedReservation from './components/ConfirmedReservation';
import DraftReservation from './components/DraftReservation';
import useReservations from './hooks/useReservations';
import UnifiedSearchFilters from '../../components/UnifiedSearchFilters/UnifiedSearchFilters';
import { getCookie } from '../../services/cookies';
import countrySettings from './countrySettings.json';

const { Option } = Select;

const ReservationSearchPage = () => {
  const [loading, setLoading] = useState({ drafts: false, confirmed: false, branchOffices: false, forecast: false });
  const draftReservations = useReservations('draft');
  const confirmedReservations = useReservations('confirmed');
  const canceledReservations = useReservations('cancel');
  const [forecast, setForecast] = useState([]);
  const [selectedTab, setSelectedTab] = useState('1')
  const [params, setParams] = useState({});
  const [branchOffices, setBranchOffices] = useState();
  const [searchCriteria, setSearchCriteria] = useState({
    start_date: moment().subtract(1, 'weeks').format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD')
  });
  const [reservationServices, setReservationServices] = useState([]);
  const [reservationPatients, setReservationPatients] = useState([]);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { RangePicker } = DatePicker;

  const fetchBranchOffices = useCallback(async () => {
    setLoading(loading => { return { ...loading, branchOffices: true } });
    try {
      const response = await getBranchOffices({ byUser: 1 });
      setBranchOffices(response.data || response || []);
    } catch (error) {
      console.error(error);
      message.error({ content: `Error interno ${error}`, style: { marginTop: '32vh' } });
    } finally {
      setLoading(loading => { return { ...loading, branchOffices: false } });
    }
  }, []);

  useEffect(() => {
    let params = { order: 'created_at' };
    if (searchCriteria) {
      params = { ...params, ...searchCriteria, from: 'search' }
    }

    draftReservations.setParams({ type: 'drafted', ...params });
    confirmedReservations.setParams({ type: 'confirmed', ...params });
    canceledReservations.setParams({ type: 'cancel', ...params });

  }, [searchCriteria]);

  useEffect(() => {
    fetchBranchOffices();
  }, [fetchBranchOffices]);

  const fetchForecast = async (reservationType) => {
    setLoading({ forecast: true });
    try {
      let response
      if (reservationType.data.length > 0) {
        const ids = reservationType.data.map((reservation) => reservation.id)
        response = await getForecastPredictionsByIds({ ...params, reservationIds: ids })
        setForecast(response);
      }
    } catch (error) {
      console.error(error);
      message.error({ content: `Error interno ${error}`, style: { marginTop: '32vh' } });
    } finally {
      setLoading({ forecast: false });
    }
  }

  useEffect(() => {
    if (draftReservations.data.length > 0 && selectedTab === '1') {
      fetchForecast(draftReservations)
    }
    if (confirmedReservations.data.length > 0 && selectedTab === '2') {
      fetchForecast(confirmedReservations);
    }
    if (canceledReservations.data.length > 0 && selectedTab === '3') {
      fetchForecast(canceledReservations);
    }
  }, [draftReservations?.data, confirmedReservations?.data, canceledReservations?.data, selectedTab]);

  const addForecastToReservations = (reservationType) => {
    setLoading({ forecast: true });
    let attendancePrediction = {}
    if (forecast?.length > 0) {
      forecast.forEach(obj => {
        try {
          if (obj?.reservation_id in attendancePrediction) {
            attendancePrediction[obj.reservation_id].push(obj.predict_percent);
          } else {
            attendancePrediction[obj.reservation_id] = [obj.predict_percent];
          }
        } catch (error) {
          console.error(error)
        }
      })
      reservationType?.data?.forEach(reservation => {
        const reservationId = reservation.id
        if (reservationId in attendancePrediction) {
          reservation.predict_percent = attendancePrediction[reservationId]
          let aboveFiftyForecast = reservation.predict_percent.map((percent) =>
            percent > 50 ? true : false
          )
          reservation.willAttend = aboveFiftyForecast.filter((value) => value === true).length
          reservation.preadmittedPatients = reservation.predict_percent.length
        }
      })
    }
    setLoading({ forecast: false });
    return reservationType
  }

  useEffect(() => {
    if (draftReservations.data.length > 0 && selectedTab === '1') {
      addForecastToReservations(draftReservations);
    }
    if (confirmedReservations.data.length > 0 && selectedTab === '2') {
      addForecastToReservations(confirmedReservations);
    }
    if (canceledReservations.data.length > 0 && selectedTab === '3') {
      addForecastToReservations(canceledReservations);
    }
  }, [forecast, selectedTab])

  const handleTabChange = (tab) => {
    setSelectedTab(tab)
  }

  const countryCode = getCookie('country_code');
  const useGranularSearchFilters = countrySettings.useGranularSearchFilters[countryCode];
  const roles = getCookie('roles') || [];
  const hasSystemWideAccess = roles.includes('direccion_medica');

  const onFinish = (values) => {
    let queryParams = {};
    if (useGranularSearchFilters) {
      queryParams = {
        ...values,
        outsourcing_id: values.outsourcing_id,
        allBranchOffices: values.allBranchOffices,
      };
    } else {
      queryParams = {
        query: values.query,
        branch_office_id: values.branch_office_id,
        way_contact_form: values.way_contact_form,
        start_date: values.dates && moment(values.dates[0], 'YYYY-MM-DD').format('YYYY-MM-DD'),
        end_date: values.dates && moment(values.dates[1], 'YYYY-MM-DD').format('YYYY-MM-DD')
      };
    }

    const paramValues = Object.fromEntries(
      Object.entries(queryParams).filter(([_, values]) => values || values === "")
    );

    if (Object.values(paramValues).length > 0) {
      setSearchCriteria(paramValues);
    } else {
      setSearchCriteria();
    }

    draftReservations.changePage(1);
    confirmedReservations.changePage(1);
  };

  const removeFilters = () => {
    form.resetFields();
    draftReservations.changePage(1);
    confirmedReservations.changePage(1);
    setSearchCriteria();
  };

  const fetchReservationServices = async (reservationId, setInnerLoading) => {
    setInnerLoading(true);
    try {
      const response = await getReservationServices({ id: reservationId });
      const orderByBlock = _.orderBy(response, 'block');
      setReservationServices(orderByBlock);
    } catch (error) {
      console.error(error);
      message.error({ content: `Error interno ${error}`, style: { marginTop: '32vh' } });
    } finally {
      setInnerLoading(false);
    }
  };

  const fetchReservationPatients = async (reservationId, setInnerLoading) => {
    setInnerLoading(true);
    try {
      const response = await summarizedPatientsByReservation({ id: reservationId });
      const orderByBlock = _.orderBy(response, 'block');
      setReservationPatients(orderByBlock);
    } catch (error) {
      console.error(error);
      message.error({ content: `Error interno ${error}`, style: { marginTop: '32vh' } });
    } finally {
      setInnerLoading(false);
    }
  };

  return (
    <Row justify="center" className="reservation-page-wrapper">
      <Col lg={24} xs={24}>
        <Row justify="center">
          <Col lg={24} xs={24} className="reservation-page-wrapper__header pt-20">
            <Row justify="center">
              <Col lg={22} xs={22}>
                <Row justify="space-between">
                  <Col className="back-button-wrapper">
                    <Link to="/reservas/fecha">
                      <i className="icon-system-chevron-left" />
                      Volver
                    </Link>
                  </Col>
                  <Col>
                    <Link to="/reservas/web">
                      <HasAccess
                        roles={['super_usuario', 'branch_office_manager', 'administracion']}
                        permissions='reservation-detail'
                      >
                        <Button className="cmt-button primary outline mb-20">
                          <svg className="mr-15" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 35.622 36.587">
                            <g id="Grupo_754" data-name="Grupo 754" transform="translate(-596.378 -1621.284)">
                              <path id="Trazado_82" data-name="Trazado 82" d="M1465.237,496.958v1.216a3.987,3.987,0,1,1-7.974,0v-1.216h-7.156v1.216a3.988,3.988,0,1,1-7.975,0v-1.216h-6.258v29.811H1471.5V496.958Z" transform="translate(-839.496 1131.102)" fill="#dc582a" />
                              <path id="Trazado_83" data-name="Trazado 83" d="M1452.976,493.49a1.284,1.284,0,1,1-2.569,0v-6.3a1.284,1.284,0,1,1,2.569,0Z" transform="translate(-845.118 1135.376)" fill="#dc582a" />
                              <path id="Trazado_84" data-name="Trazado 84" d="M1477.637,493.49a1.284,1.284,0,1,1-2.568,0v-6.3a1.284,1.284,0,1,1,2.568,0Z" transform="translate(-854.657 1135.376)" fill="#dc582a" />
                            </g>
                          </svg>
                          {t('reservations.byFilters.webReservationsButton')}
                        </Button>
                      </HasAccess>
                    </Link>
                    <Link to="/reservas/pronostico">
                      <HasAccess
                        roles={['super_usuario', 'branch_office_manager', 'administracion']}
                        permissions='reservation-forecast'
                      >
                        <Button className="cmt-button secondary">
                          <svg id="uuid-c95d951a-03f4-47ba-a30c-a2f329be5c92" data-name="Capa 2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72">
                            <g id="uuid-08ee518d-6772-4300-9ac1-7ac6e9db7128" data-name="Capa 1">
                              <g>
                                <polygon points="67.74 67.3 67.74 25.49 55.01 25.49 55.01 67.3 50.54 67.3 50.54 42.71 37.79 42.71 37.79 67.3 33.77 67.3 33.77 33.99 21.01 33.99 21.01 67.3 17.22 67.3 17.22 46.73 4.47 46.73 4.47 67.3 0 67.3 0 72 72 72 72 67.3 67.74 67.3" fill="#ffffff" stroke-width="0" />
                                <polygon points="27.91 12.88 44.15 30.05 69.16 4.35 69.16 15.92 71.84 15.92 71.84 0 54.07 0 54.07 2.68 67.03 2.68 44.18 26.17 27.99 9.05 1.17 35.09 3.04 37.01 27.91 12.88" fill="#ffffff" stroke-width="0" />
                              </g>
                            </g>
                          </svg>
                          {t('reservations.byFilters.attendanceForecastButton')}
                        </Button>
                      </HasAccess>
                    </Link>
                  </Col>
                  <Col lg={24} xs={24}>
                    <h1 className="text-center mb-10">
                      {t('reservations.byFilters.title')}
                    </h1>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
          <Col lg={24} xs={24} className="reservation-page-wrapper__body mt-lg-30 mt-md-40 mt-sm-40 mt-xs-40">
            <Row justify="center">
              <Col lg={22} xs={22}>
                {useGranularSearchFilters ? (
                  <UnifiedSearchFilters
                    moduleName={t('reservations.byFilters.title')}
                    baseFilters={['company', 'outsourcing', 'thirdCompany', 'branchOffice', 'workerId', 'workerName', 'reservationNumber', 'protocol', 'dateRange', 'evaluationType', 'allBranchOffices']}
                    customFilters={['wayContactForm', 'applicant', 'costCenter', 'timeBlock']}
                    isCollapsible={true}
                    userHasSystemWideAccess={hasSystemWideAccess}
                    onSearch={onFinish}
                    onClear={removeFilters}
                    loading={draftReservations.isLoading || confirmedReservations.isLoading}
                    initialValues={{
                      dates: [moment().subtract(1, 'weeks'), moment()],
                      branch_office_id: searchCriteria.allBranchOffices ? undefined : (searchCriteria.branch_office_id ? parseInt(searchCriteria.branch_office_id) : parseInt(getCookie('branch_office_id'))),
                      allBranchOffices: searchCriteria.allBranchOffices,
                    }}
                  />
                ) : (
                  <Form
                    form={form}
                    onFinish={onFinish}
                    size="large"
                    initialValues={{
                      dates: [moment().subtract(1, 'weeks'), moment()],
                      branch_office_id: useGranularSearchFilters && (searchCriteria.branch_office_id ? parseInt(searchCriteria.branch_office_id) : parseInt(getCookie('branch_office_id')))
                    }}
                  >
                    <Row justify="space-between" className="form-wrapper">
                      <Col xxl={8} lg={9} xs={24}>
                        <Form.Item name="query">
                          <Input className="cmt-input"
                            placeholder={t('reservations.byFilters.inputDocumentPlaceholder')}
                          />
                        </Form.Item>
                      </Col>
                      <Col xxl={4} lg={3} xs={24}>
                        {
                          loading.branchOffices ?
                            <Row justify="center">
                              <Spin className="custom-spinner" />
                            </Row>
                            :
                            <Form.Item name="branch_office_id">
                              <Select
                                className="cmt-select"
                                placeholder={t('reservations.byFilters.selectBranchOfficePlaceholder')}
                              >
                                {
                                  branchOffices && branchOffices.map((branchOffice, index) => {
                                    return (
                                      <Option value={branchOffice.id} key={index}>
                                        {branchOffice.name}
                                      </Option>
                                    )
                                  })
                                }
                              </Select>
                            </Form.Item>
                        }
                      </Col>
                      <Col xxl={4} lg={4} xs={24}>
                        <Form.Item name="way_contact_form">
                          <Select
                            className="cmt-select"
                            placeholder={t('reservations.byFilters.selectReservationMethodPlaceholder')}
                          >
                            <Option value="way_web">Web</Option>
                            <Option value="way_phone">Teléfono</Option>
                            <Option value="way_email">Correo electrónico</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xxl={4} lg={3} xs={24}>
                        <Form.Item name="dates">
                          <RangePicker
                            className="range_picker"
                            locale={locale}
                            inputReadOnly={true}
                            placeholder={['Desde', 'Hasta']}
                            popupClassName="custom-dropdown"
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col className="button-wrapper mr-lg-0 mr-md-15 mr-sm-15 mr-xs-15">
                        <Button
                          id="searchButton"
                          className="cmt-button primary"
                          type="primary"
                          loading={draftReservations.isLoading || confirmedReservations.isLoading}
                          htmlType="submit"
                        >
                          Buscar
                        </Button>
                      </Col>
                      <Col>
                        <Button className="cmt-button primary outline borderless" onClick={removeFilters}>
                          Borrar filtros
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Col>
              <Col lg={24} xs={24} className="mt-lg-15 mt-md-30 mt-sm-30 mt-xs-30">
                <Tabs className="cmt-tabs" defaultActiveKey="1" onChange={handleTabChange}>
                  <items
                    key="1"
                    tab={
                      <Badge count={draftReservations.pagination && draftReservations.pagination.total} offset={[15, 0]} showZero={true}>
                        <p className="mb-0">
                          {t('reservations.byFilters.draftTitle')}
                        </p>
                      </Badge>
                    }
                  >
                    <DraftReservation
                      draftReservations={draftReservations}
                      reservationServices={reservationServices}
                      fetchReservationServices={fetchReservationServices}
                    />

                  </items>
                  <items
                    key="2"
                    tab={
                      <Badge count={confirmedReservations.pagination && confirmedReservations.pagination.total} offset={[15, 0]} showZero={true}>
                        <p className="mb-0">
                          {t('reservations.byFilters.confirmedTitle')}
                        </p>
                      </Badge>
                    }
                  >

                    {loading.forecast ?
                      <Row justify="center">
                        <Spin className="custom-spinner" />
                      </Row>
                      :
                      <ConfirmedReservation
                        reservationServices={reservationServices}
                        reservationPatients={reservationPatients}
                        fetchReservationServices={fetchReservationServices}
                        fetchReservationPatients={fetchReservationPatients}
                        confirmedReservations={confirmedReservations}
                      />
                    }

                  </items>
                  <items
                    key="3"
                    tab={
                      <Badge count={canceledReservations.pagination && canceledReservations.pagination.total} offset={[15, 0]} showZero={true}>
                        <p className="mb-0">
                          Cancelados
                        </p>
                      </Badge>
                    }
                  >
                    <DraftReservation
                      draftReservations={canceledReservations}
                      reservationServices={reservationServices}
                      fetchReservationServices={fetchReservationServices}
                    />

                  </items>
                </Tabs>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default ReservationSearchPage;
